import { FC, useState, useCallback, useEffect, useRef } from "react";
import { MDSIcon, MDSIconKind, MDSIconColor } from "@/mds/components/icon";
import { MDSProgressBar } from "@/mds/components/loading/progress-bar";
import { MDSProgressBarColor } from "@/mds/components/loading/progress-bar/progress-bar.types";
import { MDSSeparator } from "@/mds/components/separator";
import { MDSIconButton, MDSIconButtonVariant, MDSIconButtonSize } from "@/mds/components/buttons";
import { MDSTooltip, MDSTooltipPlacement, MDSTooltipTrigger } from "@/mds/components/tooltip";
import { OnboardingTooltip } from "@/components/note/floating-actions/onboarding-tooltip";
import { GettingStartedCardProps, GettingStartedCardStep } from "./getting-started-card.types";
import {
  StyledCardContainer,
  StyledHeaderContainer,
  StyledHeader,
  StyledHeaderTitle,
  StyledExpandButton,
  StyledProgressBarContainer,
  StyledStepsContainer,
  StyledStepRow,
  StyledStepContent,
  StyledStepTitle,
  StyledStepRightIcon,
  StyledSeparatorWrapper,
  StyledCompletionContainer,
  StyledCompletionMessage,
  StyledExploreLink,
} from "./getting-started-card.style";
import { triggerCelebrationConfetti } from "@/modules/confetti";

export const GettingStartedCard: FC<GettingStartedCardProps> = ({
  steps,
  progress,
  completedCount,
  totalCount,
  className,
  initiallyCollapsed,
  onboardingTooltip,
  hasShownConfetti = false,
  onStepClick,
  onHelpClick,
  onToggleExpanded,
  onExploreMoreClick,
  isAllComplete,
  onDismiss,
  onConfettiShown,
}) => {
  const [isExpanded, setIsExpanded] = useState(!initiallyCollapsed);
  const [displayProgress, setDisplayProgress] = useState(0);

  const expandBtnRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Delay setting progress on mount for animation
  useEffect(() => {
    if (progress > 0 && progress < 100) {
      const timeout = setTimeout(() => {
        setDisplayProgress(progress);
      }, 100);
      return () => clearTimeout(timeout);
    } else {
      setDisplayProgress(progress);
    }
  }, [progress]);

  const handleToggleExpanded = useCallback(() => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onToggleExpanded?.(newExpandedState);
    onboardingTooltip?.onDismiss?.();
  }, [isExpanded, onToggleExpanded, onboardingTooltip]);

  const handleStepClick = useCallback(
    (stepId: string) => {
      onStepClick?.(stepId);
    },
    [onStepClick]
  );

  const handleHelpClick = useCallback(
    (stepId: string) => {
      onHelpClick?.(stepId);
    },
    [onHelpClick]
  );

  const handleExploreMoreClick = useCallback(() => {
    onExploreMoreClick?.();
  }, [onExploreMoreClick]);

  const handleDismiss = useCallback(() => {
    onDismiss?.();
  }, [onDismiss]);

  // Trigger confetti celebration when all steps are completed (only once)
  useEffect(() => {
    if (isAllComplete && !hasShownConfetti && cardRef.current) {
      // Small delay to ensure the UI has updated before confetti
      setTimeout(() => {
        triggerCelebrationConfetti(cardRef.current ?? undefined);
        onConfettiShown?.();
      }, 200);
    }
  }, [isAllComplete, hasShownConfetti, onConfettiShown]);

  const renderStep = useCallback(
    (step: GettingStartedCardStep, index: number) => {
      const isLastStep = index === steps.length - 1;

      const stepRow = (
        <StyledStepRow
          onClick={onStepClick && !step.isCompleted ? () => handleStepClick(step.id) : undefined}
          style={{ cursor: onStepClick && !step.isCompleted ? "pointer" : "default" }}
        >
          <StyledStepContent>
            <StyledStepTitle $isCompleted={step.isCompleted}>
              {step.title}
              {step.shouldShowHelp && (
                <MDSIconButton
                  iconKind={MDSIconKind.Help}
                  variant={MDSIconButtonVariant.Transparent}
                  size={MDSIconButtonSize.XSmall}
                  onPress={() => handleHelpClick(step.id)}
                  aria-label="Help"
                />
              )}
            </StyledStepTitle>
          </StyledStepContent>
          <StyledStepRightIcon>
            {step.isCompleted ? (
              <MDSIcon
                kind={MDSIconKind.CheckCircleSolid}
                size={14}
                color={MDSIconColor.Selected}
              />
            ) : (
              <MDSIcon kind={MDSIconKind.Circle} size={14} color={MDSIconColor.Placeholder} />
            )}
          </StyledStepRightIcon>
        </StyledStepRow>
      );

      return (
        <div key={step.id}>
          {step.tooltip ? (
            <MDSTooltip config={{ label: step.tooltip, placement: MDSTooltipPlacement.Top }}>
              <MDSTooltipTrigger>{stepRow}</MDSTooltipTrigger>
            </MDSTooltip>
          ) : (
            stepRow
          )}
          {!isLastStep && (
            <StyledSeparatorWrapper>
              <MDSSeparator />
            </StyledSeparatorWrapper>
          )}
        </div>
      );
    },
    [steps.length, onStepClick, handleStepClick, handleHelpClick]
  );

  return (
    <StyledCardContainer className={className} ref={cardRef}>
      <StyledHeaderContainer>
        <StyledHeader>
          <StyledHeaderTitle>
            Getting started ({completedCount} of {totalCount})
          </StyledHeaderTitle>
          <StyledExpandButton
            onClick={isAllComplete ? handleDismiss : handleToggleExpanded}
            aria-label={isAllComplete ? "Dismiss" : isExpanded ? "Collapse" : "Expand"}
            ref={expandBtnRef}
          >
            <MDSIcon
              kind={
                isAllComplete
                  ? MDSIconKind.Exit
                  : isExpanded
                    ? MDSIconKind.AngleDown
                    : MDSIconKind.AngleRight
              }
              size={12}
              color={MDSIconColor.Placeholder}
            />
          </StyledExpandButton>
        </StyledHeader>
        <StyledProgressBarContainer>
          <MDSProgressBar
            value={displayProgress}
            minValue={0}
            maxValue={100}
            color={MDSProgressBarColor.Emphasis}
            size="medium"
            rounded
          />
        </StyledProgressBarContainer>
      </StyledHeaderContainer>

      {isExpanded && <StyledStepsContainer>{steps.map(renderStep)}</StyledStepsContainer>}

      {isExpanded && isAllComplete && (
        <StyledCompletionContainer>
          <StyledCompletionMessage>🎉 Nice work diving into Mem.</StyledCompletionMessage>
          <StyledExploreLink onClick={handleExploreMoreClick} type="button">
            Explore more features →
          </StyledExploreLink>
        </StyledCompletionContainer>
      )}

      {onboardingTooltip && onboardingTooltip.isOpen && (
        <OnboardingTooltip
          title={onboardingTooltip.title}
          subtitle={onboardingTooltip.subtitle}
          triggerRef={{ current: expandBtnRef.current }}
          isOpen={onboardingTooltip.isOpen}
          onDismiss={onboardingTooltip.onDismiss}
          placement="left top"
          offset={24}
          pointCorner="top-left"
          width={218}
          appearanceDelay={0.7}
        />
      )}
    </StyledCardContainer>
  );
};
